import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ETypeOfNotification,
  Modal,
  RColumn,
  RemoteTable,
  RemoteTableRef,
  setNotification,
  stringFilterTemplate,
} from 'medere-ui-framework'
import { Button } from 'primereact/button'
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect'
import { useDispatch } from 'react-redux'
import { HealthEntityListItemDTO } from '../../dto/HealthEntityListItemDTO'
import {
  HealthEntityListQueryParams,
  useHealthEntityService,
} from '../../servicesHooks/HealthEntity/useHealthEntityService'
import LocationActionMenu, { LocationActionItem } from '../Location/tabs/components/LocationActionMenu'
import HealthEntityListCard, { HealthEntityCardActionsProps } from './components/HealthEntityListCard'
import HealthEntityCrudPanel from './HealthEntityCrudPanel'
import './HealthEntity.scss'

type UnknownRecord = Record<string, unknown>

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null
}

export default function HealthEntity() {
  const { deleteHealthEntity, getAllHealthEntities } = useHealthEntityService()
  const MOBILE_SEARCH_BATCH_SIZE = 200
  const MOBILE_SEARCH_MAX_SCAN = 2000
  const dispatch = useDispatch()
  const tableRef = useRef<RemoteTableRef>(null)
  const [selectedEntityId, setSelectedEntityId] = useState<number | null>(null)
  const [selectedEntityMode, setSelectedEntityMode] = useState<'edit' | 'view'>('edit')
  const [statusFilter, setStatusFilter] = useState<boolean[]>([])
  const [typeFilter, setTypeFilter] = useState<string[]>([])
  const [mobileFilterText, setMobileFilterText] = useState('')
  const typeOptions = useMemo(() => ([
    { label: 'Prestacional', value: 'Prestacional' },
    { label: 'Capitada', value: 'Capitada' },
  ]), [])
  const [deleteModal, setDeleteModal] = useState(false)
  const [entityToDelete, setEntityToDelete] = useState<number | null>(null)

  const statusOptions = useMemo(() => [
    { label: 'Activo', value: true },
    { label: 'Inactivo', value: false },
  ], [])

  function clearTableFiltersStorage() {
    const tableStateKeys = ['HealthEntitiesTable', 'mobileFilter_HealthEntitiesTable']
    tableStateKeys.forEach((key) => {
      localStorage.removeItem(key)
      sessionStorage.removeItem(key)
    })
  }

  useEffect(() => {
    // Inicia siempre limpio al entrar a "Obras Sociales".
    clearTableFiltersStorage()

    // Limpia al salir del apartado.
    return () => {
      clearTableFiltersStorage()
    }
  }, [])

  useEffect(() => {
    tableRef.current?.refreshTable?.()
  }, [mobileFilterText])

  function matchesMobileFilter(item: HealthEntityListItemDTO, normalizedFilterText: string) {
    if (normalizedFilterText === '') return true

    const searchableValues = [
      item.commercialname,
      item.businessname,
      item.typelabel,
      item.healthentitystatedesc,
      item.active ? 'activo' : 'inactivo',
    ]

    return searchableValues.some((value) => String(value ?? '').toLowerCase().includes(normalizedFilterText))
  }

  const openDelete = useCallback((id: number) => {
    setEntityToDelete(id)
    setDeleteModal(true)
  }, [])

  const loadHealthEntities = useCallback(async (offSet: number, pageSize: number, params: HealthEntityListQueryParams = {}) => {
    const normalizedMobileFilterText = mobileFilterText.trim().toLowerCase()
    const shouldApplyClientMobileFilter = normalizedMobileFilterText !== ''

    const selectedStatuses: boolean[] = params?.healthEntityStatus ?? [true, false]
    const selectedTypeFilter: string[] = params?.typeFilter ?? []

    if (!shouldApplyClientMobileFilter) {
      return getAllHealthEntities(offSet, pageSize, {
        ...params,
        healthEntityStatus: selectedStatuses,
        typeFilter: selectedTypeFilter,
      })
    }

    const fetchSize = Math.max(MOBILE_SEARCH_BATCH_SIZE, pageSize)
    let fetchOffset = 0
    let items: HealthEntityListItemDTO[] = []

    while (items.length < MOBILE_SEARCH_MAX_SCAN) {
      const batch = await getAllHealthEntities(fetchOffset, fetchSize, {
        ...params,
        healthEntityStatus: selectedStatuses,
        typeFilter: selectedTypeFilter,
      })
      if (batch.length === 0) break

      items = [...items, ...batch]
      if (batch.length < fetchSize) break
      fetchOffset += fetchSize
    }

    const filteredItems = items.filter((item) => matchesMobileFilter(item, normalizedMobileFilterText))
    return filteredItems.slice(offSet, offSet + pageSize)
  }, [mobileFilterText])

  function confirmDelete() {
    if (entityToDelete == null) {
      setDeleteModal(false)
      return
    }
    deleteHealthEntity(entityToDelete).then(() => {
      dispatch(setNotification({
        title: 'Baja de obra social',
        desc: 'La obra social se eliminó correctamente.',
        typeOfNotification: ETypeOfNotification.INFO,
        icon: 'pi pi-check',
      }))
      tableRef.current?.refreshTable?.()
    }).catch((error: unknown) => {
      const response = isRecord(error) && isRecord(error.response) ? error.response : null
      const status = typeof response?.status === 'number' ? response.status : null

      if (status === 409) {
        dispatch(setNotification({
          title: 'No se puede eliminar',
          desc: 'La obra social está siendo utilizada en otras relaciones.',
          typeOfNotification: ETypeOfNotification.WARNING,
          icon: 'pi pi-exclamation-triangle',
        }))
        return
      }

      dispatch(setNotification({
        title: 'Error',
        desc: 'No se pudo eliminar la obra social',
        typeOfNotification: ETypeOfNotification.ERROR,
        icon: 'pi pi-times',
      }))
    }).finally(() => {
      setDeleteModal(false)
      setEntityToDelete(null)
    })
  }

  const openView = useCallback((item: HealthEntityListItemDTO) => {
    setSelectedEntityMode('view')
    setSelectedEntityId(item.medereentity)
  }, [])

  const openEdit = useCallback((item: HealthEntityListItemDTO) => {
    setSelectedEntityMode('edit')
    setSelectedEntityId(item.medereentity)
  }, [])

  function getFilterText(input: unknown): string {
    if (typeof input === 'string') return input
    if (!isRecord(input)) return ''
    const target = isRecord(input.target) ? input.target : null
    return typeof target?.value === 'string' ? target.value : ''
  }

  const handleMobileFilterChange = useCallback((input: unknown) => {
    const filterValue = getFilterText(input)
    setMobileFilterText(filterValue)
  }, [])

  const handleClearFilters = useCallback(() => {
    setStatusFilter([])
    setTypeFilter([])
  }, [])

  const handleTableClearFiltersClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target
    if (!(target instanceof HTMLElement)) {
      return
    }

    const clearButton = target.closest('button[aria-label="Limpiar filtros"]')
    if (!clearButton) {
      return
    }

    clearTableFiltersStorage()
    window.setTimeout(() => {
      handleClearFilters()
    }, 0)
  }, [handleClearFilters])

  const cardActionsProps: HealthEntityCardActionsProps = useMemo(() => ({
    onView: openView,
    onEdit: openEdit,
    onDelete: (item) => openDelete(item.medereentity),
  }), [openDelete, openEdit, openView])

  const tableParams = useMemo(() => ({
    healthEntityStatus: statusFilter.length > 0 ? statusFilter : [true, false],
    typeFilter,
  }), [statusFilter, typeFilter])

  const tableRenderKey = useMemo(
    () => `health-entity-table-${typeOptions.map((option) => option.value).join('|')}--${typeFilter.join('|')}--${statusFilter.join('|')}`,
    [statusFilter, typeFilter, typeOptions],
  )

  const rowActions = useCallback((rowData: HealthEntityListItemDTO) => {
    const menuItems: LocationActionItem[] = [
      {
        label: 'Ver detalle',
        icon: 'pi pi-eye',
        action: () => openView(rowData),
      },
      {
        label: 'Editar',
        icon: 'pi pi-file-edit',
        action: () => openEdit(rowData),
      },
      {
        label: 'Eliminar',
        icon: 'pi pi-trash',
        action: () => openDelete(rowData.medereentity),
      },
    ]

    return (
      <LocationActionMenu
        items={menuItems}
        ariaLabel='Opciones de obra social'
        buttonId={`healthEntityMenu_${rowData.medereentity}`}
        buttonClassName='p-button-text'
      />
    )
  }, [openDelete, openEdit, openView])

  const typeFilterElement = useCallback(() => (
    <MultiSelect
      options={typeOptions}
      optionLabel='label'
      optionValue='value'
      value={typeFilter}
      onChange={(e: MultiSelectChangeEvent) => setTypeFilter((e.value ?? []) as string[])}
      className='w-full health-entity-filter-multiselect'
      style={{ height: '2.25rem', marginBottom: '0.28rem' }}
    />
  ), [typeFilter, typeOptions])

  const statusFilterElement = useCallback(() => (
    <MultiSelect
      options={statusOptions}
      optionLabel='label'
      optionValue='value'
      value={statusFilter}
      onChange={(e: MultiSelectChangeEvent) => setStatusFilter((e.value ?? []) as boolean[])}
      className='w-full health-entity-filter-multiselect'
      style={{ height: '2.25rem', marginBottom: '0.28rem' }}
    />
  ), [statusFilter, statusOptions])

  if (selectedEntityId != null) {
    return (
      <HealthEntityCrudPanel
        healthEntityId={selectedEntityId}
        setHealthEntityId={setSelectedEntityId}
        mode={selectedEntityMode}
        setMode={setSelectedEntityMode}
        onSaved={() => tableRef.current?.refreshTable?.()}
      />
    )
  }

  return (
    <div className='viewContainer health-entity-view' onClick={handleTableClearFiltersClick}>
      <div className='health-entity-header'>
        <h1 className='health-entity-title'>Obras Sociales</h1>
        <Button
          id='buttonCreateHealthEntityId'
          type='button'
          className='buttonMain margin-create-button'
          label='Crear'
          icon='pi pi-plus-circle'
          onClick={() => {
            setSelectedEntityMode('edit')
            setSelectedEntityId(-1)
          }} />
      </div>
      <div className='health-entity-divider' />

      <RemoteTable
        key={tableRenderKey}
        idTable='HealthEntitiesTable'
        dataKey='medereentity'
        className='health-entity-table'
        getItems={loadHealthEntities}
        resizableColumns
        dynamicBehaviour
        CardTemplate={HealthEntityListCard}
        cardKeyName='medereentity'
        cardAdditionalProps={cardActionsProps}
        handleMobileFilter={handleMobileFilterChange}
        params={tableParams}
        ref={tableRef}
        filterRow
        rowActions={rowActions} >
        <RColumn field='commercialname' className='column' header='Nombre Comercial' filter filterElement={stringFilterTemplate} />
        <RColumn field='businessname' className='column' header='Razón Social' filter filterElement={stringFilterTemplate} />
        <RColumn field='typelabel' className='column' header='Tipo' filter filterElement={typeFilterElement} />
        <RColumn field='active' className='column' header='Estado' body={(item: HealthEntityListItemDTO) => item.active ? 'Activo' : 'Inactivo'} filter filterElement={statusFilterElement} />
      </RemoteTable>

      <Modal
        visible={deleteModal}
        setVisible={setDeleteModal}
        header='Confirma eliminar la obra social?'
        footerButtonRightText='Eliminar'
        footerButtonLeftText='Cancelar'
        onClickRightBtn={confirmDelete}
        onClickLeftBtn={() => setDeleteModal(false)} />
    </div>
  )
}
